import React from "react";

import { motion } from "framer-motion";

import SparklineChart from "../charts/SparklineChart";

const KPICard = ({
  label,
  value,
  delta,
  deltaLabel,
  icon,
  color,
  sparklineData,
  target,
  loading,
  index,
}) => {
  // Delta badge: green for positive, red for negative, gray for neutral
  const getDeltaBadge = () => {
    if (delta == null) return null;

    const numDelta = typeof delta === "string" ? parseFloat(delta) : delta;
    const displayDelta =
      typeof delta === "string" ? delta : `${Math.abs(numDelta).toFixed(1)}%`;

    if (numDelta > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
          <i className="fas fa-arrow-up text-[10px]" />
          {displayDelta}
          {deltaLabel && (
            <span className="text-gray-400 dark:text-gray-500">
              {deltaLabel}
            </span>
          )}
        </span>
      );
    }

    if (numDelta < 0) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
          <i className="fas fa-arrow-down text-[10px]" />
          {displayDelta}
          {deltaLabel && (
            <span className="text-gray-400 dark:text-gray-500">
              {deltaLabel}
            </span>
          )}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
        0%
        {deltaLabel && (
          <span className="text-gray-400 dark:text-gray-500">{deltaLabel}</span>
        )}
      </span>
    );
  };

  // Circular progress indicator for target
  const renderTargetProgress = () => {
    if (target == null) return null;

    const numValue =
      typeof value === "string"
        ? parseFloat(value.replace(/[^0-9.-]/g, ""))
        : value;
    const pct = Math.min((numValue / target) * 100, 100);
    const radius = 16;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (pct / 100) * circumference;

    return (
      <div className="flex flex-col items-center gap-0.5">
        <svg
          className="transform -rotate-90"
          height="40"
          viewBox="0 0 40 40"
          width="40"
        >
          <circle
            className="text-gray-200 dark:text-gray-700"
            cx="20"
            cy="20"
            fill="none"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
          />
          <circle
            className="text-primary-500"
            cx="20"
            cy="20"
            fill="none"
            r={radius}
            stroke="currentColor"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            strokeWidth="3"
            style={{ transition: "stroke-dashoffset 0.5s ease" }}
          />
        </svg>
        <span className="text-[10px] text-gray-500 dark:text-gray-400">
          {Math.round(pct)}%
        </span>
      </div>
    );
  };

  // Loading skeleton
  if (loading) {
    return (
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800/50 rounded-xl shadow-md border border-gray-100 dark:border-gray-700/50 p-4"
        initial={{ opacity: 0, y: 20 }}
        transition={{ delay: (index || 0) * 0.1 }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full animate-pulse bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1 space-y-2">
            <div className="h-5 w-20 animate-pulse bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-3 w-24 animate-pulse bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-3 w-14 animate-pulse bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800/50 rounded-xl shadow-md border border-gray-100 dark:border-gray-700/50 p-4"
      initial={{ opacity: 0, y: 20 }}
      transition={{ delay: (index || 0) * 0.1 }}
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div
          className={`w-10 h-10 rounded-full ${color || "bg-primary-500"} flex items-center justify-center flex-shrink-0`}
        >
          <i className={`${icon} text-white`} />
        </div>

        {/* Center: value, label, delta */}
        <div className="flex-1 min-w-0">
          <p className="text-xl font-bold text-gray-900 dark:text-white truncate">
            {value}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-300 truncate">
            {label}
          </p>
          {getDeltaBadge()}
        </div>

        {/* Right: sparkline or target */}
        <div className="flex-shrink-0">
          {sparklineData && sparklineData.length > 0 && (
            <div className="w-20">
              <SparklineChart color={color} data={sparklineData} />
            </div>
          )}
          {!sparklineData && target != null && renderTargetProgress()}
        </div>
      </div>
    </motion.div>
  );
};

export default KPICard;
